import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, MessageSquare, ChevronUp, ChevronDown } from 'lucide-react';
import { ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { financialStatementApi } from '../../services/financialStatementApi';
import type { FinancialStatementLine, LineAccount, LineReposting } from '../../types/financialStatement';
import { CommentBadge } from './CommentBadge';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    line: FinancialStatementLine;
    company: string;
    year: number;
    comparisonYear: number;
    onAccountClick: (account: LineAccount) => void;
    onCommentClick: (lineNr: number, accountNr?: string) => void;
}

function formatAmount(amount: number): string {
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(Math.round(amount));
}

type SortKey = 'AccountNr' | 'AccountName' | 'CurrentYearAmount' | 'ComparisonYearAmount' | 'VarianceCHF';
type SortDir = 'asc' | 'desc';

function SortHeader({ label, sortKey, currentKey, currentDir, onSort, align }: {
    label: string;
    sortKey: SortKey;
    currentKey: SortKey;
    currentDir: SortDir;
    onSort: (key: SortKey) => void;
    align?: 'left' | 'right';
}) {
    const isActive = currentKey === sortKey;
    return (
        <th
            className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 bg-white ${
                align === 'right' ? 'text-right' : 'text-left'
            }`}
            onClick={() => onSort(sortKey)}
        >
            <span className="inline-flex items-center gap-0.5">
                {align === 'right' && (
                    <span className="inline-flex flex-col -space-y-1">
                        <ChevronUp className={`h-3 w-3 ${isActive && currentDir === 'asc' ? 'text-gray-900' : 'text-gray-300'}`} />
                        <ChevronDown className={`h-3 w-3 ${isActive && currentDir === 'desc' ? 'text-gray-900' : 'text-gray-300'}`} />
                    </span>
                )}
                {label}
                {align !== 'right' && (
                    <span className="inline-flex flex-col -space-y-1">
                        <ChevronUp className={`h-3 w-3 ${isActive && currentDir === 'asc' ? 'text-gray-900' : 'text-gray-300'}`} />
                        <ChevronDown className={`h-3 w-3 ${isActive && currentDir === 'desc' ? 'text-gray-900' : 'text-gray-300'}`} />
                    </span>
                )}
            </span>
        </th>
    );
}

export function DrillThroughModal({
    isOpen, onClose, line, company, year, comparisonYear,
    onAccountClick, onCommentClick,
}: Props) {
    const [accounts, setAccounts] = useState<LineAccount[]>([]);
    const [repostings, setRepostings] = useState<LineReposting[]>([]);
    const [loading, setLoading] = useState(false);
    const [sortKey, setSortKey] = useState<SortKey>('AccountNr');
    const [sortDir, setSortDir] = useState<SortDir>('asc');

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        financialStatementApi.getLineAccounts({
            lineNr: line.LineNr,
            company,
            year,
            comparisonYear,
        })
            .then(result => {
                setAccounts(result.accounts);
                setRepostings(result.repostings || []);
            })
            .catch(err => console.error('Failed to load line accounts:', err))
            .finally(() => setLoading(false));
    }, [isOpen, line.LineNr, company, year, comparisonYear]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const sorted = useMemo(() => {
        const list = [...accounts];
        list.sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (typeof av === 'string' && typeof bv === 'string') {
                return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
            }
            const an = av as number, bn = bv as number;
            return sortDir === 'asc' ? an - bn : bn - an;
        });
        return list;
    }, [accounts, sortKey, sortDir]);

    if (!isOpen) return null;

    const exportUrl = financialStatementApi.getLineAccountsExportUrl({
        lineNr: line.LineNr,
        company,
        year,
        comparisonYear,
    });

    const total = accounts.reduce((s, a) => s + a.CurrentYearAmount, 0);
    const totalComp = accounts.reduce((s, a) => s + a.ComparisonYearAmount, 0);
    const repostingTotal = repostings.reduce((s, r) => s + r.CurrentYearAmount, 0);
    const repostingTotalComp = repostings.reduce((s, r) => s + r.ComparisonYearAmount, 0);
    const hasRepostings = repostings.length > 0;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {line.LineNr} - {line.LineNameEnglish || line.LineNameGerman}
                        </h2>
                        <p className="text-sm text-gray-500">
                            {company.toUpperCase()} | {year} vs {comparisonYear}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => onCommentClick(line.LineNr)}
                            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                        >
                            <MessageSquare className="h-4 w-4" />
                            Comments
                        </button>
                        <a
                            href={exportUrl}
                            className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1.5 text-sm text-white hover:bg-green-700"
                        >
                            <Download className="h-4 w-4" />
                            Export
                        </a>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="overflow-auto overscroll-contain flex-1 px-6 pb-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                        </div>
                    ) : (
                        <table className="min-w-full border-separate border-spacing-0">
                            <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.08)]">
                                {/* Total row */}
                                <tr className="border-b border-gray-200">
                                    <td className="px-3 py-2 text-sm font-bold bg-white" colSpan={2}>
                                        Total ({accounts.length} accounts)
                                    </td>
                                    <td className="px-3 py-2 text-sm text-right font-bold tabular-nums bg-white">{formatAmount(total)}</td>
                                    <td className="px-3 py-2 text-sm text-right font-bold tabular-nums text-gray-500 bg-white">{formatAmount(totalComp)}</td>
                                    <td className="px-3 py-2 text-sm text-right font-bold tabular-nums bg-white">{formatAmount(total - totalComp)}</td>
                                    <td className="bg-white"></td>
                                </tr>
                                <tr className="border-b-2 border-gray-200">
                                    <SortHeader label="Account" sortKey="AccountNr" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Name" sortKey="AccountName" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label={String(year)} sortKey="CurrentYearAmount" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label={String(comparisonYear)} sortKey="ComparisonYearAmount" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Variance" sortKey="VarianceCHF" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                                    <th className="px-2 py-2 w-10 bg-white"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sorted.map(acc => (
                                    <tr
                                        key={acc.AccountNr}
                                        className="hover:bg-blue-50 cursor-pointer"
                                        onClick={() => onAccountClick(acc)}
                                    >
                                        <td className="px-3 py-1.5 text-sm font-mono">{acc.AccountNr}</td>
                                        <td className="px-3 py-1.5 text-sm">{acc.AccountName}</td>
                                        <td className="px-3 py-1.5 text-sm text-right tabular-nums">{formatAmount(acc.CurrentYearAmount)}</td>
                                        <td className="px-3 py-1.5 text-sm text-right tabular-nums text-gray-500">{formatAmount(acc.ComparisonYearAmount)}</td>
                                        <td className={`px-3 py-1.5 text-sm text-right tabular-nums ${
                                            acc.VarianceCHF > 0 ? 'text-green-600' : acc.VarianceCHF < 0 ? 'text-red-600' : ''
                                        }`}>{formatAmount(acc.VarianceCHF)}</td>
                                        <td className="px-2 py-1.5">
                                            <CommentBadge
                                                count={acc.CommentCount}
                                                onClick={() => onCommentClick(line.LineNr, acc.AccountNr)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Repostings section */}
                    {!loading && hasRepostings && (
                        <div className="mt-4 border-t border-dashed border-gray-300 pt-3">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3">
                                Repostings
                            </h3>
                            <table className="min-w-full">
                                <tbody className="divide-y divide-gray-100">
                                    {repostings.map((r, i) => {
                                        const variance = r.CurrentYearAmount - r.ComparisonYearAmount;
                                        return (
                                            <tr key={i} className="bg-amber-50/50">
                                                <td className="px-3 py-1.5 text-sm">
                                                    {r.Direction === 'from' ? (
                                                        <ArrowDownLeft className="h-3.5 w-3.5 text-green-600 inline mr-1" />
                                                    ) : (
                                                        <ArrowUpRight className="h-3.5 w-3.5 text-red-600 inline mr-1" />
                                                    )}
                                                    <span className="text-gray-400 mr-1">{r.Direction === 'from' ? 'From' : 'To'} {r.OtherLineNr}</span>
                                                </td>
                                                <td className="px-3 py-1.5 text-sm">
                                                    {r.OtherLineName}
                                                    <span className="ml-2 text-xs text-gray-400 italic">{r.Reason}</span>
                                                </td>
                                                <td className={`px-3 py-1.5 text-sm text-right tabular-nums ${
                                                    r.CurrentYearAmount > 0 ? 'text-green-600' : r.CurrentYearAmount < 0 ? 'text-red-600' : ''
                                                }`}>{formatAmount(r.CurrentYearAmount)}</td>
                                                <td className={`px-3 py-1.5 text-sm text-right tabular-nums text-gray-500`}>
                                                    {formatAmount(r.ComparisonYearAmount)}
                                                </td>
                                                <td className={`px-3 py-1.5 text-sm text-right tabular-nums ${
                                                    variance > 0 ? 'text-green-600' : variance < 0 ? 'text-red-600' : ''
                                                }`}>{formatAmount(variance)}</td>
                                                <td className="px-2 py-1.5 w-10"></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t border-gray-300 font-semibold">
                                        <td className="px-3 py-2 text-sm" colSpan={2}>Adjusted Total</td>
                                        <td className="px-3 py-2 text-sm text-right tabular-nums">{formatAmount(total + repostingTotal)}</td>
                                        <td className="px-3 py-2 text-sm text-right tabular-nums text-gray-500">{formatAmount(totalComp + repostingTotalComp)}</td>
                                        <td className="px-3 py-2 text-sm text-right tabular-nums">{formatAmount((total + repostingTotal) - (totalComp + repostingTotalComp))}</td>
                                        <td className="px-2 py-2 w-10"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {!loading && accounts.length === 0 && (
                        <div className="text-center py-8 text-gray-500">No accounts mapped to this line.</div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
